/**
 * O16 -- footer "Join the table" newsletter form.
 *
 * Submits directly to Klaviyo's client-side subscriptions API. No Shopify
 * customer record is created -- this is Klaviyo-only sign-up.
 *
 * Endpoint: POST https://a.klaviyo.com/client/subscriptions/?company_id=<public key>
 * Docs: https://developers.klaviyo.com/en/reference/subscribe_profiles
 *
 * The company (public API key) and list ID are read from data-attributes on
 * each `form[data-o16-klaviyo]`, set from section settings in footer.liquid,
 * so the owner can repoint sign-ups to a different Klaviyo list from the
 * Theme Customizer without touching code.
 */
(function () {
	'use strict';

	var KLAVIYO_REVISION = '2024-10-15';
	var EMAIL_PATTERN = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
	var DEFAULT_ERROR = 'Something went wrong. Please try again.';
	var DEFAULT_SUCCESS = "Thanks — you're on the list.";

	function isValidEmail(value) {
		return EMAIL_PATTERN.test(value);
	}

	function showError(form, message) {
		var errorEl = form.querySelector('[data-o16-klaviyo-error]');
		var successEl = form.querySelector('[data-o16-klaviyo-success]');
		if (successEl) {
			successEl.hidden = true;
		}
		if (errorEl) {
			var textEl = errorEl.querySelector('[data-o16-klaviyo-error-text]');
			if (textEl) {
				textEl.textContent = message;
			}
			errorEl.hidden = false;
		}
	}

	function showSuccess(form) {
		var errorEl = form.querySelector('[data-o16-klaviyo-error]');
		var successEl = form.querySelector('[data-o16-klaviyo-success]');
		if (errorEl) {
			errorEl.hidden = true;
		}
		if (successEl) {
			var message = form.getAttribute('data-success-text') || DEFAULT_SUCCESS;
			successEl.textContent = message;
			successEl.hidden = false;
			successEl.focus();
		}
	}

	function setLoading(form, isLoading) {
		var button = form.querySelector('button[type="submit"]');
		var input = form.querySelector('input[type="email"]');
		form.dataset.o16Submitting = isLoading ? 'true' : 'false';
		if (button) {
			button.disabled = isLoading;
			if (isLoading) {
				button.setAttribute('aria-busy', 'true');
			} else {
				button.removeAttribute('aria-busy');
			}
		}
		if (input) {
			input.disabled = isLoading;
		}
	}

	function parseErrorDetail(payload) {
		if (
			payload &&
			Array.isArray(payload.errors) &&
			payload.errors.length &&
			payload.errors[0].detail
		) {
			return payload.errors[0].detail;
		}
		return null;
	}

	function handleSubmit(event) {
		event.preventDefault();

		var form = event.currentTarget;

		if (form.dataset.o16Submitting === 'true') {
			return;
		}

		var input = form.querySelector('input[type="email"]');
		var email = input ? input.value.trim() : '';

		if (!isValidEmail(email)) {
			showError(form, 'Please enter a valid email address.');
			if (input) {
				input.focus();
			}
			return;
		}

		var companyId = form.getAttribute('data-klaviyo-company');
		var listId = form.getAttribute('data-klaviyo-list');

		if (!companyId || !listId) {
			showError(form, 'Sign-up is temporarily unavailable. Please try again later.');
			return;
		}

		setLoading(form, true);

		var endpoint =
			'https://a.klaviyo.com/client/subscriptions/?company_id=' +
			encodeURIComponent(companyId);

		var body = {
			data: {
				type: 'subscription',
				attributes: {
					custom_source: 'Footer – Join the table',
					profile: {
						data: {
							type: 'profile',
							attributes: { email: email }
						}
					}
				},
				relationships: {
					list: {
						data: { type: 'list', id: listId }
					}
				}
			}
		};

		fetch(endpoint, {
			method: 'POST',
			headers: {
				revision: KLAVIYO_REVISION,
				'content-type': 'application/json'
			},
			body: JSON.stringify(body)
		})
			.then(function (response) {
				if (response.status === 202) {
					showSuccess(form);
					if (input) {
						input.value = '';
					}
					return;
				}

				return response
					.json()
					.catch(function () {
						return null;
					})
					.then(function (payload) {
						console.error('[O16 Klaviyo] subscribe failed', response.status, payload);
						showError(form, parseErrorDetail(payload) || DEFAULT_ERROR);
					});
			})
			.catch(function (error) {
				console.error('[O16 Klaviyo] network error', error);
				showError(form, 'Please check your connection and try again.');
			})
			.finally(function () {
				setLoading(form, false);
			});
	}

	function init() {
		var forms = document.querySelectorAll('form[data-o16-klaviyo]');
		forms.forEach(function (form) {
			form.addEventListener('submit', handleSubmit);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
